'use client'

import { InputField, type InputFieldProps } from '@stone-payments/jade';
import { forwardRef } from 'react';

interface FormatedInputProps extends InputFieldProps {
  formatter: 'date' | 'cvv' | 'brazilianStates' | 'cardHolder' | 'addressNumber' | 'numeric' | 'zipCode';
  maxLength?: number;
}

const dateFormatter = (value: string) => {
  value = value.replace(/\D/g, '');

  if (value.length > 2) {
    value = `${value.slice(0, 2)}/${value.slice(2)}`;
  }

  return value.slice(0, 5);
};

const cvvFormatter = (value: string, maxLength = 4) => {
  const numericValue = value.replace(/\D/g, '');

  return numericValue.slice(0, maxLength);
}


const brazilianStatesFormatter = (inputValue: string) => {
  const onlyLetters = inputValue.replace(/[^A-Za-z]/g, '');

  const value = onlyLetters.toUpperCase();

  if (/^[A-Z]{0,2}$/.test(value)) {
    return value;
  } else {
    return value.slice(0, 2);
  }
};

const numericFormatter = (value: string) => {
  return value.replace(/[^0-9]/g, '');
}

const cardHolderFormatter = (value: string) => {
  const withoutDiacritics = value.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const onlyLettersAndSpace = withoutDiacritics.replace(/[^A-Za-z\s]/g, '');

  return onlyLettersAndSpace.toUpperCase();
};

const addressNumberFormatter = (value: string) => {
  return value.replace(/[^a-zA-Z0-9]/g, '');
};

const zipCodeFormatter = (value: string) => {
  return value.replace(/[^a-zA-Z0-9\s-]/g, '').toUpperCase();
};

const formatterMap: Record<FormatedInputProps['formatter'], (value: string, maxLength?: number) => string> = {
  cvv: cvvFormatter,
  date: dateFormatter,
  brazilianStates: brazilianStatesFormatter,
  cardHolder: cardHolderFormatter,
  addressNumber: addressNumberFormatter,
  numeric: numericFormatter,
  zipCode: zipCodeFormatter,
}

export const FormattedInput = forwardRef<HTMLInputElement, FormatedInputProps>(function MaskedInput(
  { formatter, onChange, defaultValue, value, maxLength, ...rest },
  ref
) {
  const formatFn = formatterMap[formatter];

  return (
    <InputField
      ref={ref}
      value={value ? formatFn(value, maxLength) : undefined}
      defaultValue={defaultValue ? formatFn(defaultValue as string, maxLength) : undefined}
      onChange={e => {
        e.target.value = formatFn(e.target.value, maxLength);
        if (onChange) onChange(e);
      }}
      {...rest}
    />
  );
});
